import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  subscribeToNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  archiveNotification,
  getUnreadNotificationCount,
  getNotificationIcon,
  getNotificationColor,
  formatNotificationTime
} from '../services/notificationService';

const NotificationCenter = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // State management
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, messages, assignments
  
  // Refs
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  
  // Subscribe to real-time notifications
  useEffect(() => {
    if (!currentUser) return;

    setLoading(true);
    
    const unsubscribe = subscribeToNotifications(
      currentUser.uid,
      (newNotifications) => {
        setNotifications(newNotifications);
        
        // Calculate unread count
        const unread = newNotifications.filter(n => !n.isRead).length;
        setUnreadCount(unread);
        
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if unread
      if (!notification.isRead) {
        await markNotificationAsRead(notification.id);
      }

      // Navigate to relevant page
      if (notification.metadata?.actionUrl) {
        navigate(notification.metadata.actionUrl);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(currentUser.uid);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Archive notification
  const handleArchiveNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await archiveNotification(notificationId);
    } catch (error) {
      console.error('Error archiving notification:', error);
    }
  };

  // Filter notifications
  const getFilteredNotifications = () => {
    switch (filter) {
      case 'unread':
        return notifications.filter(n => !n.isRead);
      case 'messages':
        return notifications.filter(n => n.type === 'message');
      case 'assignments':
        return notifications.filter(n => n.type === 'assignment');
      default:
        return notifications;
    }
  };

  const filteredNotifications = getFilteredNotifications();

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Notification Bell Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          padding: '8px',
          cursor: 'pointer',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s ease',
          fontSize: '20px'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'transparent';
        }}
      >
        {/* Professional Bell Icon */}
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          style={{ color: '#374151' }}
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        
        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <div style={{
            position: 'absolute',
            top: '2px',
            right: '2px',
            backgroundColor: '#dc2626',
            color: 'white',
            borderRadius: '50%',
            width: '18px',
            height: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: 'bold',
            minWidth: '18px'
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            width: '380px',
            maxHeight: '500px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e5e7eb',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #f3f4f6',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#1a1a1a'
              }}>
                Notifications
              </h3>
              
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6366f1',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            <div style={{
              display: 'flex',
              gap: '8px'
            }}>
              {[
                { key: 'all', label: 'All', count: notifications.length },
                { key: 'unread', label: 'Unread', count: unreadCount },
                { key: 'messages', label: 'Messages', count: notifications.filter(n => n.type === 'message').length },
                { key: 'assignments', label: 'Tasks', count: notifications.filter(n => n.type === 'assignment').length }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: filter === tab.key ? '#6366f1' : 'transparent',
                    color: filter === tab.key ? 'white' : '#6b7280',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {tab.label} {tab.count > 0 && `(${tab.count})`}
                </button>
              ))}
            </div>
          </div>

          {/* Notifications List */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            maxHeight: '400px'
          }}>
            {loading ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '14px'
              }}>
                Loading notifications...
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#6b7280'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#9ca3af' }}>
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                </div>
                <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                  {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                </div>
                <div style={{ fontSize: '12px' }}>
                  {filter === 'all' ? 'You\'ll see updates about messages, assignments, and more here.' : ''}
                </div>
              </div>
            ) : (
              <div>
                {filteredNotifications.map(notification => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      backgroundColor: notification.isRead ? 'white' : '#f0f9ff',
                      transition: 'background-color 0.2s ease',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = notification.isRead ? '#f9fafb' : '#e0f2fe';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = notification.isRead ? 'white' : '#f0f9ff';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}>
                      {/* Notification Icon */}
                      <div style={{
                        fontSize: '20px',
                        flexShrink: 0,
                        marginTop: '2px'
                      }}>
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Notification Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: notification.isRead ? '500' : 'bold',
                          color: '#1a1a1a',
                          marginBottom: '4px',
                          lineHeight: '1.4'
                        }}>
                          {notification.title}
                        </div>
                        
                        <div style={{
                          fontSize: '13px',
                          color: '#6b7280',
                          marginBottom: '6px',
                          lineHeight: '1.4'
                        }}>
                          {notification.message}
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{
                            fontSize: '11px',
                            color: '#9ca3af'
                          }}>
                            {formatNotificationTime(notification.createdAt)}
                          </div>
                          
                          {notification.priority === 'high' && (
                            <div style={{
                              fontSize: '10px',
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontWeight: '500'
                            }}>
                              HIGH
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Archive Button */}
                      <button
                        onClick={(e) => handleArchiveNotification(notification.id, e)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#9ca3af',
                          fontSize: '16px',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '4px',
                          flexShrink: 0,
                          opacity: 0,
                          transition: 'opacity 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#f3f4f6';
                          e.target.style.color = '#6b7280';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#9ca3af';
                        }}
                      >
                        ×
                      </button>
                    </div>

                    {/* Unread Indicator */}
                    {!notification.isRead && (
                      <div style={{
                        position: 'absolute',
                        left: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '6px',
                        height: '6px',
                        backgroundColor: getNotificationColor(notification.type, notification.priority),
                        borderRadius: '50%'
                      }} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {filteredNotifications.length > 0 && (
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid #f3f4f6',
              backgroundColor: '#f9fafb',
              textAlign: 'center'
            }}>
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6366f1',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hover effect for archive buttons */}
      <style jsx>{`
        div:hover .archive-button {
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

export default NotificationCenter; 